import type { BufferAttribute, BufferGeometry, InterleavedBufferAttribute } from "three";
import { isInterleavedBufferAttribute } from "./helper.js";

// #region Geometry Size

export interface GeometryMemoryEstimate {
    /** CPU memory in bytes (the attribute arrays three.js keeps in JS memory). */
    cpu: number;
    /** GPU VRAM in bytes (the uploaded buffer copies). */
    vram: number;
}

/**
 * Estimates the memory usage of a BufferGeometry: the sum of all attribute
 * buffers, the index buffer and morph attribute buffers. Interleaved
 * attributes share one underlying InterleavedBuffer — it is counted once.
 *
 * three.js keeps the typed arrays in JS memory after upload (unless the app
 * clears them manually), so CPU and VRAM estimates are the same total.
 */
export function estimateGeometryMemory(geometry: BufferGeometry): GeometryMemoryEstimate {
    let bytes = 0;
    // interleaved attributes share a buffer — count each backing array once
    const seenArrays = new Set<object>();

    const addAttribute = (attribute: BufferAttribute | InterleavedBufferAttribute | null | undefined) => {
        if (!attribute) return;
        const array = isInterleavedBufferAttribute(attribute)
            ? attribute.data?.array
            : (attribute as BufferAttribute).array;
        if (!array || typeof (array as ArrayBufferView).byteLength !== "number") return;
        if (seenArrays.has(array)) return;
        seenArrays.add(array);
        bytes += (array as ArrayBufferView).byteLength;
    };

    for (const name in geometry.attributes) {
        addAttribute(geometry.attributes[name] as BufferAttribute | InterleavedBufferAttribute);
    }
    addAttribute(geometry.index);
    if (geometry.morphAttributes) {
        for (const name in geometry.morphAttributes) {
            const morphs = geometry.morphAttributes[name];
            if (Array.isArray(morphs)) {
                for (const morph of morphs) addAttribute(morph);
            }
        }
    }

    return { cpu: bytes, vram: bytes };
}

// #endregion
